<template>
  <div class="purchase-form-card">
    <div class="purchase-block-title">出行配置</div>

    <div v-if="showDatePicker" class="field-block">
      <div class="field-label">选择日期</div>
      <el-date-picker
        :value="datePickerValue"
        :type="datePickerType"
        :picker-options="datePickerOptions"
        align="left"
        placement="bottom-start"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        placeholder="请选择出行日期"
        style="width: 100%;"
        @input="$emit('date-input', $event)"
      ></el-date-picker>
      <div class="field-tip" v-if="saleStartDate || saleEndDate">
        可售日期：{{ saleStartDate || '-' }} 至 {{ saleEndDate || '-' }}
      </div>
    </div>

    <div v-else class="field-block field-static">
      <div class="field-label">服务日期</div>
      <div class="field-static-value">{{ displayDateText || '以开放日期规则为准' }}</div>
    </div>

    <div v-if="hasTimeSlots" class="field-block">
      <div class="field-label">选择时间</div>
      <div class="chip-list">
        <div
          v-for="slot in timeSlots"
          :key="slot"
          class="chip-item"
          :class="{ active: selectedTimeSlot === slot }"
          @click="$emit('select-time-slot', slot)"
        >
          {{ getTimeSlotName(slot) }}
        </div>
      </div>
    </div>

    <div v-if="availableSpecs.length" class="field-block">
      <div class="field-label">选择规格</div>

      <div v-for="(spec, specIndex) in availableSpecs" :key="spec.spec_oid" class="spec-group">
        <div class="spec-title">{{ spec.spec_title }}</div>

        <div
          v-if="lastSpecMulti && specIndex === availableSpecs.length - 1"
          class="multi-spec-list"
        >
          <div
            v-for="specItem in spec.spec_items"
            :key="specItem.spec_item_oid"
            class="multi-spec-item"
            :class="{ disabled: !isSpecItemAvailableForDate(spec.spec_oid, specItem.spec_item_oid) }"
          >
            <div class="multi-spec-info">
              <div class="multi-spec-name">{{ specItem.name }}</div>
              <div class="multi-spec-price">¥{{ getSpecItemPrice(spec.spec_oid, specItem.spec_item_oid) }}</div>
            </div>
            <div class="qty-stepper">
              <button
                type="button"
                class="qty-btn"
                :disabled="getSpecItemQuantity(spec.spec_oid, specItem.spec_item_oid) <= 0"
                @click="$emit('decrease-spec-quantity', spec.spec_oid, specItem.spec_item_oid)"
              >
                -
              </button>
              <span class="qty-value">{{ getSpecItemQuantity(spec.spec_oid, specItem.spec_item_oid) }}</span>
              <button
                type="button"
                class="qty-btn"
                :disabled="!isSpecItemAvailableForDate(spec.spec_oid, specItem.spec_item_oid)"
                @click="$emit('increase-spec-quantity', spec.spec_oid, specItem.spec_item_oid)"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div v-else class="chip-list">
          <div
            v-for="specItem in spec.spec_items"
            :key="specItem.spec_item_oid"
            class="chip-item"
            :class="{
              active: selectedSpecs[spec.spec_oid] === specItem.spec_item_oid,
              disabled: !isSpecItemAvailableForDate(spec.spec_oid, specItem.spec_item_oid),
            }"
            @click="$emit('select-spec', spec.spec_oid, specItem.spec_item_oid)"
          >
            {{ specItem.name }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!lastSpecMulti && matchedSkus.length" class="field-block">
      <div class="field-label">选择数量</div>
      <div class="sku-list">
        <div
          v-for="sku in matchedSkus"
          :key="sku.sku_id"
          class="sku-item"
          :class="{ disabled: !isSkuAvailableForDate(sku) }"
        >
          <div class="sku-info">
            <div class="sku-name">{{ sku.display_name || sku.sku_name }}</div>
            <div class="sku-price">¥{{ getSkuFinalPriceB2C(sku) }}</div>
          </div>
          <div class="qty-stepper">
            <button
              type="button"
              class="qty-btn"
              :disabled="sku.quantity <= 0"
              @click="$emit('decrease-sku-quantity', sku)"
            >
              -
            </button>
            <span class="qty-value">{{ sku.quantity || 0 }}</span>
            <button
              type="button"
              class="qty-btn"
              :disabled="!isSkuAvailableForDate(sku)"
              @click="$emit('increase-sku-quantity', sku)"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShopPurchaseSpecSelector',
  props: {
    showDatePicker: Boolean,
    datePickerValue: {
      type: [Array, String],
      default: '',
    },
    datePickerType: {
      type: String,
      default: 'date',
    },
    datePickerOptions: {
      type: Object,
      default() {
        return {};
      },
    },
    saleStartDate: {
      type: String,
      default: '',
    },
    saleEndDate: {
      type: String,
      default: '',
    },
    displayDateText: {
      type: String,
      default: '',
    },
    hasTimeSlots: Boolean,
    timeSlots: {
      type: Array,
      default() {
        return [];
      },
    },
    selectedTimeSlot: {
      type: String,
      default: '',
    },
    availableSpecs: {
      type: Array,
      default() {
        return [];
      },
    },
    lastSpecMulti: Boolean,
    selectedSpecs: {
      type: Object,
      default() {
        return {};
      },
    },
    matchedSkus: {
      type: Array,
      default() {
        return [];
      },
    },
    getTimeSlotName: {
      type: Function,
      required: true,
    },
    getSpecItemPrice: {
      type: Function,
      required: true,
    },
    getSpecItemQuantity: {
      type: Function,
      required: true,
    },
    isSpecItemAvailableForDate: {
      type: Function,
      required: true,
    },
    getSkuFinalPriceB2C: {
      type: Function,
      required: true,
    },
    isSkuAvailableForDate: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style scoped>
.purchase-form-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
  padding: 20px 22px;
}

.purchase-block-title {
  color: #20314a;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.field-block + .field-block,
.spec-group + .spec-group {
  margin-top: 18px;
}

.field-label,
.spec-title {
  color: #334155;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
}

.field-tip {
  margin-top: 8px;
  color: #8b95a7;
  font-size: 12px;
}

.field-static {
  padding: 14px 16px;
  border: 1px solid #e8eef7;
  border-radius: 10px;
  background: #f8fafc;
}

.field-static-value {
  color: #1c5098;
  font-weight: bold;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.chip-item {
  padding: 10px 16px;
  border-radius: 22px;
  border: 1px solid #d9e4f2;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  user-select: none;
  transition: 0.2s ease;
}

.chip-item.active {
  border-color: #1c5098;
  background: #1c5098;
  color: #fff;
}

.chip-item.disabled {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #b4bcc8;
  cursor: not-allowed;
}

.sku-list,
.multi-spec-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sku-item,
.multi-spec-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px solid #e8eef7;
  border-radius: 10px;
  background: #fbfdff;
  gap: 12px;
}

.sku-item.disabled,
.multi-spec-item.disabled {
  opacity: 0.55;
}

.sku-info,
.multi-spec-info {
  min-width: 0;
}

.sku-name,
.multi-spec-name {
  color: #223046;
  font-size: 14px;
  font-weight: bold;
}

.sku-price,
.multi-spec-price {
  margin-top: 6px;
  color: #1c5098;
  font-size: 18px;
  font-weight: bold;
}

.qty-stepper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.qty-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #c9d7ea;
  border-radius: 50%;
  background: #fff;
  color: #1c5098;
  font-size: 18px;
  cursor: pointer;
}

.qty-btn:disabled {
  color: #c0c7d4;
  border-color: #e3e8ef;
  cursor: not-allowed;
}

.qty-value {
  min-width: 18px;
  text-align: center;
  color: #223046;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/components/ShopPurchaseSpecSelector.vue